import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IMcpSession, McpSession } from '../models/mcp-session.model';

@Injectable({
  providedIn: 'root'
})
export class McpSessionService {

  public loading: boolean = false;
  public mcpSessions: McpSession[] = [];

  /* Sin spinnerService a propósito: pinta un overlay que bloquea la pantalla
     completa. La lista se dibuja con esqueletos y cerrar una sesión muestra su
     progreso en el propio botón. Tampoco se pide nada en el constructor: la
     página llama a getAll() al entrar.

     No hay método para GET /mcp-sessions/{mcpSessionId}: la lista ya trae todo lo que
     pinta la pantalla y no existe vista de detalle. */
  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public toastService: ToastService
  ) { }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  // region delete
  async delete(
    mcpSession: McpSession
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlApi}/mcp-sessions/${mcpSession.mcpSessionId}`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ mcpSession: IMcpSession }>>(
        (resolve, reject) => {
          this.http.delete<IServerResponse<{ mcpSession: IMcpSession }>>(
            urlApi,
            {
              headers: { 'Authorization': token.toString() }
            }
          ).subscribe({
            next: (response: IServerResponse<{ mcpSession: IMcpSession }>) =>
              resolve(new ServerResponse<{ mcpSession: IMcpSession }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;

        /* La sesión deja de existir en la API: se quita de la lista local (array
           nuevo) sin re-pedirla, así la tabla no vuelve a esqueleto. */
        this.mcpSessions = this.mcpSessions.filter(
          item => item.mcpSessionId !== mcpSession.mcpSessionId
        );

        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    return flag;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll(): Promise<void> {
    this.loading = true;
    const urlApi = `${environment.urlApi}/mcp-sessions`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ mcpSessions: IMcpSession[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ mcpSessions: IMcpSession[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse<{ mcpSessions: IMcpSession[] }>) =>
              resolve(new ServerResponse<{ mcpSessions: IMcpSession[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        /* Ya vienen ordenadas por createdAt desc: se respeta ese orden. */
        this.mcpSessions = response.data!.mcpSessions.map(
          item => new McpSession(item)
        );
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
    } catch (error) {
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;
  }

}
